import React, { useState } from 'react';
import { authClient } from '../lib/auth';

interface SecuritySettingsProps {
  onClose: () => void;
}

export const SecuritySettings: React.FC<SecuritySettingsProps> = ({ onClose }) => {
  const [totpUri, setTotpUri] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      // Generate TOTP secret and get URI for QR code
      const res = await authClient.twoFactor.enable({
        password: password,
      });
      if (res.error) {
        setError(res.error.message || 'Error');
      } else if (res.data && 'totpURI' in res.data && res.data.totpURI) {
        setTotpUri(res.data.totpURI);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to generate 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await authClient.twoFactor.verifyTotp({
        code: verificationCode,
      });
      if (res.error) {
        setError(res.error.message || 'Error');
      } else {
        setSuccess(true);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="glass-panel" style={{ padding: '2.5rem', width: '100%', maxWidth: '500px', background: 'var(--bg-primary)' }}>
        <h2 style={{ fontSize: '1.5rem', margin: '0 0 1.5rem 0' }}>Security Settings</h2>
        
        {success ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', color: '#10b981', marginBottom: '1rem' }}>✓</div>
            <h3 style={{ margin: '0 0 1rem 0' }}>2FA Enabled Successfully</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
              Your account is now protected with Two-Factor Authentication. You will be required to enter a code from your authenticator app when signing in.
            </p>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            {error && (
              <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: '8px', marginBottom: '1.5rem' }}>
                {error}
              </div>
            )}

            {!totpUri ? (
              <form onSubmit={handleGenerate}>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                  Enable Two-Factor Authentication using an authenticator app (like Google Authenticator, Authy, or 1Password). Please enter your password to continue.
                </p>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Current Password</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={loading}>
                    {loading ? 'Processing...' : 'Set Up 2FA'}
                  </button>
                  <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose} disabled={loading}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerify}>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                  1. Scan the QR code with your authenticator app.<br/>
                  2. Enter the 6-digit verification code below.
                </p>
                
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem', padding: '1rem', background: 'white', borderRadius: '8px' }}>
                  {/* For MVP, we display the raw URI or use a simple QR code generator API. In production, use a local QR library. */}
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpUri)}`} 
                    alt="2FA QR Code" 
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Verification Code</label>
                  <input
                    type="text"
                    required
                    maxLength={6}
                    pattern="\d{6}"
                    placeholder="123456"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', textAlign: 'center', fontSize: '1.25rem', letterSpacing: '0.5rem' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={loading}>
                    {loading ? 'Verifying...' : 'Verify & Enable'}
                  </button>
                  <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setTotpUri(null)} disabled={loading}>
                    Back
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};
